import { z } from 'zod';

import { CompanyIndustry } from '../../../../types/company.types';
import {
  LOGO_FILE_MIME_TYPES,
  urlSchemaWithoutProtocol,
  zodFileSchema,
} from '../../../../types/general.types';
import { getPlainTextFromHtml } from '../../../../utils/get-plain-text-from-html.util';
import { PerkClaimDisplayRule, PerkClaimType, PerkOfferType, RegionType } from '../perks.types';

export const PHOTO_IMAGE_ACCEPT = {
  'image/svg+xml': ['.svg'],
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/svg': ['.svg'],
  'image/png': ['.png'],
};

const stepOne = z.object({
  companyName: z
    .string()
    .min(1, 'Company name is required')
    .max(50, 'Company name exceeds the limit of 50 characters'),
  industry: z.enum(CompanyIndustry),
  url: urlSchemaWithoutProtocol,
  contactPerson: z.email('Email format is incorrect'),
  applicableRegions: z
    .object({ iso2: z.string(), name: z.string(), type: z.enum(RegionType) })
    .array()
    .optional(),
});

export const stepTwo = z.object({
  companySlogan: z
    .string()
    .min(1, 'Short description is required')
    .max(60, 'Company slogan exceeds the limit of 50 characters'),
  companyShortDescription: z
    .string()
    .min(1, 'Short description is required')
    .max(100, 'Short description exceeds the limit of 100 characters'),
  companyDescription: z
    .string()
    .min(1, 'Long description is required')
    .refine((description) => getPlainTextFromHtml(description).length <= 700, {
      message: 'Long description exceeds the limit of 700 characters',
    }),
});

export const stepThree = z.object({
  offerDescription: z
    .string()
    .min(1, 'Long description is required')
    .refine((description) => getPlainTextFromHtml(description).length <= 700, {
      message: 'Offer description exceeds the limit of 700 characters',
    }),
});

export const stepFour = z.object({
  claim: z.discriminatedUnion('type', [
    z.object({
      type: z.literal(PerkClaimType.PROMO_CODE),
      code: z.string().min(1, 'Code description is required'),
      displayRule: z.enum(PerkClaimDisplayRule),
    }),
    z.object({
      type: z.literal(PerkClaimType.LINK),
      url: urlSchemaWithoutProtocol,
    }),
  ]),
  offerTypes: z.enum(PerkOfferType).array().optional(),
});

export const stepFive = z.object({
  logo: zodFileSchema({ fileMimeTypes: LOGO_FILE_MIME_TYPES }),
  banner: zodFileSchema({ fileMimeTypes: LOGO_FILE_MIME_TYPES }),
});

export const perkFormSchema = z.object({
  stepOne,
  stepTwo,
  stepThree,
  stepFour,
  stepFive,
});

export type PerkFormSchema = z.infer<typeof perkFormSchema>;
